import { Truck, Ticket, Sparkles, Coins } from "lucide-react";
import { formasPagamento, getNormalizedStatus } from "../../lib/pedidoConstants";
import { formaBase, useNomeForma } from "../../lib/formasDaLoja";

/**
 * Footer com breakdown (subtotal / frete / desconto / ajuste residual), total
 * destacado e linha de pagamento(s) com ícone + troco quando aplicável.
 */
export function PedidoTotaisFooter({ pedido }) {
  return (
    <div className="border-t px-4 py-3 shrink-0 space-y-2">
      <Breakdown pedido={pedido} />
      <TotalRow pedido={pedido} />
    </div>
  );
}

function Breakdown({ pedido }) {
  const items = pedido.items || [];
  const itemsSubtotal = items.reduce(
    (s, it) =>
      s +
      Number(
        it.final_price ?? it.preco_unitario ?? it.unit_price ?? it.sale_price ?? it.preco ?? 0,
      ) *
        Number(it.quantity || it.quantidade || 1),
    0,
  );
  const cupomDesc = Number(pedido.cupom_desconto || 0);
  const manualDesc = Number(pedido.desconto_manual || 0);
  const totalDesc = cupomDesc + manualDesc;
  const cashbackUsado = Number(pedido.cashback_usado || 0);
  const taxaEntrega = Number(pedido.valor_entrega || pedido.taxaEntrega || 0);
  const totalFinal = Number(pedido.total || 0);
  // Cashback entra na conta esperada — sem isso ele aparecia como "Ajuste" fantasma
  const expected = itemsSubtotal + taxaEntrega - totalDesc - cashbackUsado;
  const ajusteResidual = totalFinal - expected;
  const hasAdjust = taxaEntrega > 0 || totalDesc > 0 || cashbackUsado > 0 || Math.abs(ajusteResidual) >= 0.01;
  if (!hasAdjust) return null;

  const isDelivery = pedido.tipoEntrega === "delivery" || !!pedido.endereco;
  const isFreteResidual = isDelivery && ajusteResidual > 0 && taxaEntrega === 0;

  return (
    <div className="rounded-md border bg-muted/30 px-2.5 py-2 text-xs space-y-1">
      <Row
        label={`Subtotal (${items.length} ${items.length === 1 ? "item" : "itens"})`}
        value={`R$ ${itemsSubtotal.toFixed(2).replace(".", ",")}`}
      />
      {taxaEntrega > 0 && (
        <Row
          icon={Truck}
          label="Taxa de entrega"
          value={`+ R$ ${taxaEntrega.toFixed(2).replace(".", ",")}`}
        />
      )}
      {totalDesc > 0 && (
        <DescontoRow
          pedido={pedido}
          itemsSubtotal={itemsSubtotal}
          cupomDesc={cupomDesc}
          manualDesc={manualDesc}
          totalDesc={totalDesc}
        />
      )}
      {cashbackUsado > 0 && (
        <div className="flex items-center justify-between text-emerald-700 dark:text-emerald-400">
          <span className="flex items-center gap-1.5 font-semibold">
            <Coins className="w-3 h-3 shrink-0" /> Cashback
          </span>
          <span className="font-mono tabular-nums font-bold shrink-0">
            − R$ {cashbackUsado.toFixed(2).replace(".", ",")}
          </span>
        </div>
      )}
      {Math.abs(ajusteResidual) >= 0.01 && (
        <AjusteRow ajuste={ajusteResidual} isFreteResidual={isFreteResidual} />
      )}
    </div>
  );
}

function DescontoRow({ pedido, itemsSubtotal, cupomDesc, manualDesc, totalDesc }) {
  const cupomCod = pedido.cupom_codigo;
  const tipo = pedido.desconto_tipo || (cupomDesc > 0 ? "coupon" : "fixed");
  const pct = pedido.desconto_percentual && Number(pedido.desconto_percentual) > 0
    ? Number(pedido.desconto_percentual)
    : tipo === "percent" && itemsSubtotal > 0 ? (manualDesc / itemsSubtotal) * 100 : null;
  const cupomPct = cupomCod && itemsSubtotal > 0 ? (cupomDesc / itemsSubtotal) * 100 : null;
  const fmtP = (v) => v.toFixed(v % 1 === 0 ? 0 : 1).replace(".", ",");

  let label;
  if (cupomCod) {
    label = cupomPct != null
      ? <>Cupom <span className="font-mono font-bold">{cupomCod}</span> <span className="opacity-80">· {fmtP(cupomPct)}%</span></>
      : <>Cupom <span className="font-mono font-bold">{cupomCod}</span></>;
  } else if (tipo === "percent" && pct != null) {
    label = `Desconto manual (${fmtP(pct)}%)`;
  } else {
    label = "Desconto manual";
  }

  return (
    <div className="flex items-center justify-between text-green-700 dark:text-green-400">
      <span className="flex items-center gap-1.5 font-semibold min-w-0">
        <Ticket className="w-3 h-3 shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <span className="font-mono tabular-nums font-bold shrink-0">
        − R$ {totalDesc.toFixed(2).replace(".", ",")}
      </span>
    </div>
  );
}

function AjusteRow({ ajuste, isFreteResidual }) {
  const cls = ajuste < 0
    ? "text-green-700 dark:text-green-400"
    : isFreteResidual
      ? "text-muted-foreground"
      : "text-amber-700 dark:text-amber-400";
  const Icon = isFreteResidual ? Truck : Sparkles;
  return (
    <div className={`flex items-center justify-between ${cls}`}>
      <span className="flex items-center gap-1.5 font-medium">
        <Icon className="w-3 h-3" /> {isFreteResidual ? "Frete" : "Ajuste"}
      </span>
      <span className="font-mono tabular-nums">
        {ajuste < 0 ? "−" : "+"} R$ {Math.abs(ajuste).toFixed(2).replace(".", ",")}
      </span>
    </div>
  );
}

function TotalRow({ pedido }) {
  const isCancel = getNormalizedStatus(pedido.status) === "cancelado";
  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-col gap-0.5">
        <PagamentosLines pedido={pedido} />
      </div>
      <span className={`font-bold text-lg ${isCancel ? "text-red-600 dark:text-red-400 line-through" : "text-primary"}`}>
        R$ {(pedido.total || 0).toFixed(2).replace(".", ",")}
      </span>
    </div>
  );
}

// O código da forma ("dinheiro_<loja>") é interno: aparece o nome da forma.
function PagamentosLines({ pedido }) {
  const nomeForma = useNomeForma();
  const formaDe = (v) => formasPagamento[v] || formasPagamento[formaBase(v)];
  const nomeDe = (v) => formasPagamento[v]?.label || nomeForma(v);
  if (pedido.pagamentos?.length) {
    return (
      <div className="space-y-0.5">
        {pedido.pagamentos.map((pg, i) => {
          const Icon = formaDe(pg.method)?.icon;
          return (
            <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
              {Icon && <Icon className="w-3.5 h-3.5" />}
              <span>{nomeDe(pg.method)}</span>
              <span className="font-semibold">R$ {(pg.amount || 0).toFixed(2).replace(".", ",")}</span>
              {pg.troco_precisa && pg.troco_valor && (
                <span className="text-green-600">
                  (troco R$ {Math.max(0, Number(pg.troco_valor) - Number(pg.amount || 0)).toFixed(2).replace(".", ",")} — paga com R$ {Number(pg.troco_valor).toFixed(2).replace(".", ",")})
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  const Icon = formaDe(pedido.formaPagamento)?.icon;
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span>{nomeDe(pedido.formaPagamento)}</span>
      {pedido.troco?.precisa && pedido.troco.valor && (
        <span className="text-green-600">
          (troco R$ {Math.max(0, Number(pedido.troco.valor) - Number(pedido.total || 0)).toFixed(2).replace(".", ",")} — paga com R$ {Number(pedido.troco.valor).toFixed(2).replace(".", ",")})
        </span>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-muted-foreground">
      <span className="flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />} {label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}
